<div>
  <div class="container-fluid">
    <!-- Back Button -->
    <div class="mb-3">
      <button type="button" class="btn btn-outline-secondary" (click)="goBack()">
        <i class="bi bi-arrow-left me-2"></i>
        Back to Sponsors
      </button>
    </div>

    <!-- Loading State -->
    <div *ngIf="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <!-- Error Message -->
    <div *ngIf="errorMessage" class="alert alert-danger alert-dismissible fade show" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="errorMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Success Message -->
    <div *ngIf="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
      <i class="bi bi-check-circle-fill me-2"></i>
      {{ successMessage }}
      <button type="button" class="btn-close" aria-label="Close" (click)="successMessage = ''" aria-label="Close"></button>
    </div>

    <!-- Sponsor Details -->
    <div *ngIf="!loading && sponsor">
      <!-- Header Card -->
      <div class="card mb-4">
        <div class="card-body">
          <div class="row align-items-center">
            <div class="col-auto">
              <!-- Logo preview -->
              <div
                class="sponsor-logo-container entity-avatar entity-avatar-square overflow-hidden p-2"
                [class.border]="!sponsor.brandColor"
                [style.background-color]="sponsor.brandColor || null"
              >
                <img
                  *ngIf="sponsor.logoUrl"
                  [src]="getLogoFullUrl(sponsor.logoUrl)"
                  [alt]="sponsor.name + ' logo'"
                  class="mw-100 mh-100 object-fit-contain"
                />
                <div *ngIf="!sponsor.logoUrl" class="fs-4 text-body-secondary">
                  <i class="bi bi-image"></i>
                </div>
              </div>
            </div>
            <div class="col">
              <h2 class="mb-1">{{ sponsor.name }}</h2>
              <div class="d-flex flex-wrap align-items-center gap-3 text-muted">
                <span *ngIf="sponsor.website">
                  <i class="bi bi-globe me-1"></i>
                  <a [href]="sponsor.website" target="_blank" rel="noopener">{{ sponsor.website }}</a>
                </span>
                <span *ngIf="sponsor.phone">
                  <i class="bi bi-telephone me-1"></i>
                  {{ sponsor.phone | phoneFormat }}
                </span>
                <span *ngIf="sponsor.email">
                  <i class="bi bi-envelope me-1"></i>
                  {{ sponsor.email }}
                </span>
              </div>
            </div>
            <div class="col-auto">
              <a [routerLink]="['/sponsors']" class="btn btn-outline-secondary me-2">
                <i class="bi bi-list me-1"></i>
                All Sponsors
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- Tabs + Content (single card container) -->
      <div class="card">
        <div class="card-header p-0">
          <ul id="sponsor-detail-tabs" class="nav nav-underline sponsor-tabs px-3 pt-2 flex-nowrap overflow-x-auto" role="tablist">
            <li class="nav-item">
              <button
                class="nav-link sponsor-tab-btn d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'info'"
                type="button"
                role="tab"
                (click)="setActiveTab('info')"
              >
                <i class="bi bi-info-circle me-2"></i>
                Info
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link sponsor-tab-btn d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'locations'"
                type="button"
                role="tab"
                (click)="setActiveTab('locations')"
              >
                <i class="bi bi-geo-alt me-2"></i>
                Locations
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link sponsor-tab-btn d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'representatives'"
                type="button"
                role="tab"
                (click)="setActiveTab('representatives')"
              >
                <i class="bi bi-people me-2"></i>
                Representatives
              </button>
            </li>
            <li class="nav-item">
              <button
                class="nav-link sponsor-tab-btn d-flex align-items-center px-3 text-nowrap"
                [class.active]="activeTab === 'rates'"
                type="button"
                role="tab"
                (click)="setActiveTab('rates')"
              >
                <i class="bi bi-star me-2"></i>
                Rates
              </button>
            </li>
          </ul>
        </div>

        <!-- Tab Content -->
        <div class="card-body">

          <!-- ==========================================
               INFO TAB
               ========================================== -->
          <div *ngIf="activeTab === 'info'">
            <!-- Sponsor Settings Row: Logo + Brand Color -->
            <div class="row mb-4">
              <!-- Logo Upload -->
              <div class="col-md-6">
                <div>
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-image me-2"></i>
                    Sponsor Logo
                  </h6>
                  <div class="d-flex align-items-center gap-3">
                    <div
                      class="sponsor-logo-preview d-flex align-items-center justify-content-center rounded-2 p-2"
                      [class.has-brand-color]="sponsor.brandColor"
                      [style.background-color]="sponsor.brandColor || null"
                    >
                      <img
                        *ngIf="sponsor.logoUrl"
                        [src]="getLogoFullUrl(sponsor.logoUrl)"
                        [alt]="sponsor.name + ' logo'"
                        class="img-fluid"
                        style="max-height: 80px;"
                      />
                      <span *ngIf="!sponsor.logoUrl" class="text-muted">No logo uploaded</span>
                    </div>
                    <div>
                      <label class="btn btn-outline-primary btn-sm" [class.disabled]="uploadingLogo">
                        <i class="bi bi-upload me-1"></i>
                        {{ sponsor.logoUrl ? 'Change Logo' : 'Upload Logo' }}
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg,.svg"
                          (change)="onLogoFileSelected($event)"
                          class="d-none"
                          [disabled]="uploadingLogo"
                        />
                      </label>
                      <div *ngIf="uploadingLogo" class="mt-2">
                        <div class="spinner-border spinner-border-sm text-primary" role="status">
                          <span class="visually-hidden">Uploading...</span>
                        </div>
                        <span class="ms-2 text-muted small">Uploading...</span>
                      </div>
                      <div class="text-muted small mt-1">PNG, JPG, JPEG, or SVG</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Brand Color -->
              <div class="col-md-6">
                <div>
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-palette me-2"></i>
                    Brand Color
                  </h6>
                  <div *ngIf="!editingBrandColor" class="d-flex align-items-center gap-3">
                    <div
                      class="d-inline-block p-3 rounded-2 border flex-shrink-0"
                      [style.background-color]="sponsor.brandColor || '#cccccc'"
                    ></div>
                    <span>{{ sponsor.brandColor || 'Not set' }}</span>
                    <button class="btn btn-outline-primary btn-sm ms-auto" (click)="startEditBrandColor()">
                      <i class="bi bi-pencil me-1"></i>
                      Edit
                    </button>
                  </div>
                  <div *ngIf="editingBrandColor">
                    <div class="d-flex align-items-center gap-3 mb-3">
                      <input
                        type="color"
                        class="form-control form-control-color"
                        [(ngModel)]="brandColorValue"
                        title="Choose brand color"
                      />
                      <input
                        type="text"
                        class="form-control"
                        [(ngModel)]="brandColorValue"
                        placeholder="#000000"
                        style="max-width: 150px;"
                      />
                    </div>
                    <div class="d-flex gap-2">
                      <button class="btn btn-primary btn-sm" (click)="saveBrandColor()">Save</button>
                      <button class="btn btn-outline-secondary btn-sm" (click)="cancelEditBrandColor()">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Website -->
            <div class="py-3 border-bottom mb-4">
              <h6 class="fw-semibold mb-3">
                <i class="bi bi-globe me-2"></i>
                Website
              </h6>
              <div *ngIf="!editingWebsite" class="d-flex align-items-center gap-3">
                <span *ngIf="sponsor.website">
                  <a [href]="sponsor.website" target="_blank" rel="noopener">{{ sponsor.website }}</a>
                </span>
                <span *ngIf="!sponsor.website" class="text-muted">No website set</span>
                <button class="btn btn-outline-primary btn-sm ms-auto" (click)="startEditWebsite()">
                  <i class="bi bi-pencil me-1"></i>
                  {{ sponsor.website ? 'Edit' : 'Add Website' }}
                </button>
              </div>
              <div *ngIf="editingWebsite">
                <input
                  type="url"
                  class="form-control mb-3"
                  [(ngModel)]="websiteValue"
                  placeholder="https://example.com"
                  style="max-width: 400px;"
                />
                <div class="d-flex gap-2">
                  <button class="btn btn-primary btn-sm" (click)="saveWebsite()">Save</button>
                  <button class="btn btn-outline-secondary btn-sm" (click)="cancelEditWebsite()">Cancel</button>
                </div>
              </div>
            </div>

            <!-- Phone & Email Row -->
            <div class="row mb-4">
              <div class="col-md-6">
                <div>
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-telephone me-2"></i>
                    Phone
                  </h6>
                  <div *ngIf="!editingPhone" class="d-flex align-items-center gap-3">
                    <span *ngIf="sponsor.phone">{{ sponsor.phone | phoneFormat }}</span>
                    <span *ngIf="!sponsor.phone" class="text-muted">No phone set</span>
                    <button class="btn btn-outline-primary btn-sm ms-auto" (click)="startEditPhone()">
                      <i class="bi bi-pencil me-1"></i>
                      {{ sponsor.phone ? 'Edit' : 'Add Phone' }}
                    </button>
                  </div>
                  <div *ngIf="editingPhone">
                    <input
                      type="tel"
                      class="form-control mb-3"
                      [(ngModel)]="phoneValue"
                      mask="(000) 000-0000"
                      [validation]="false"
                      placeholder="(555) 123-4567"
                      style="max-width: 250px;"
                    />
                    <div class="d-flex gap-2">
                      <button class="btn btn-primary btn-sm" (click)="savePhone()">Save</button>
                      <button class="btn btn-outline-secondary btn-sm" (click)="cancelEditPhone()">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-6">
                <div>
                  <h6 class="fw-semibold mb-3">
                    <i class="bi bi-envelope me-2"></i>
                    Email
                  </h6>
                  <div *ngIf="!editingEmail" class="d-flex align-items-center gap-3">
                    <span *ngIf="sponsor.email">{{ sponsor.email }}</span>
                    <span *ngIf="!sponsor.email" class="text-muted">No email set</span>
                    <button class="btn btn-outline-primary btn-sm ms-auto" (click)="startEditEmail()">
                      <i class="bi bi-pencil me-1"></i>
                      {{ sponsor.email ? 'Edit' : 'Add Email' }}
                    </button>
                  </div>
                  <div *ngIf="editingEmail">
                    <input
                      type="email"
                      class="form-control mb-3"
                      [(ngModel)]="emailValue"
                      placeholder="contact@example.com"
                      style="max-width: 300px;"
                    />
                    <div class="d-flex gap-2">
                      <button class="btn btn-primary btn-sm" (click)="saveEmail()">Save</button>
                      <button class="btn btn-outline-secondary btn-sm" (click)="cancelEditEmail()">Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Description -->
            <div class="py-3 border-bottom mb-4">
              <h6 class="fw-semibold mb-3">
                <i class="bi bi-card-text me-2"></i>
                Description
              </h6>
              <div *ngIf="!editingDescription">
                <p *ngIf="sponsor.description" class="mb-2">{{ sponsor.description }}</p>
                <span *ngIf="!sponsor.description" class="text-muted">No description set</span>
                <div class="mt-2">
                  <button class="btn btn-outline-primary btn-sm" (click)="startEditDescription()">
                    <i class="bi bi-pencil me-1"></i>
                    {{ sponsor.description ? 'Edit' : 'Add Description' }}
                  </button>
                </div>
              </div>
              <div *ngIf="editingDescription">
                <textarea
                  class="form-control mb-3"
                  [(ngModel)]="descriptionValue"
                  rows="3"
                  placeholder="Enter a description for this sponsor (shown on the Acre Advantage dashboard)"
                ></textarea>
                <div class="d-flex gap-2">
                  <button class="btn btn-primary btn-sm" (click)="saveDescription()">Save</button>
                  <button class="btn btn-outline-secondary btn-sm" (click)="cancelEditDescription()">Cancel</button>
                </div>
              </div>
            </div>

            <!-- Contact Type -->
            <div class="py-3 border-bottom mb-4">
              <h6 class="fw-semibold mb-3">
                <i class="bi bi-telephone-forward me-2"></i>
                Contact Type
              </h6>
              <div *ngIf="!editingContactType" class="d-flex align-items-center gap-3">
                <span class="badge bg-primary">{{ getContactTypeLabel(sponsor.contactType) }}</span>
                <button class="btn btn-outline-primary btn-sm ms-auto" (click)="startEditContactType()">
                  <i class="bi bi-pencil me-1"></i>
                  Edit
                </button>
              </div>
              <div *ngIf="editingContactType">
                <select class="form-select mb-3" [(ngModel)]="contactTypeValue" style="max-width: 320px;">
                  <option *ngFor="let opt of contactTypeOptions" [value]="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
                <div class="d-flex gap-2">
                  <button class="btn btn-primary btn-sm" (click)="saveContactType()">Save</button>
                  <button class="btn btn-outline-secondary btn-sm" (click)="cancelEditContactType()">Cancel</button>
                </div>
              </div>
            </div>

            <!-- Contact Message -->
            <div class="pt-3">
              <h6 class="fw-semibold mb-3">
                <i class="bi bi-chat-left-text me-2"></i>
                Contact Message
              </h6>
              <div *ngIf="!editingContactMessage">
                <p *ngIf="sponsor.contactMessage" class="mb-2">{{ sponsor.contactMessage }}</p>
                <span *ngIf="!sponsor.contactMessage" class="text-muted">
                  No custom message set. Default: "Contact this sponsor for pricing in your area."
                </span>
                <div class="mt-2">
                  <button class="btn btn-outline-primary btn-sm" (click)="startEditContactMessage()">
                    <i class="bi bi-pencil me-1"></i>
                    {{ sponsor.contactMessage ? 'Edit' : 'Add Message' }}
                  </button>
                </div>
              </div>
              <div *ngIf="editingContactMessage">
                <textarea
                  class="form-control mb-3"
                  [(ngModel)]="contactMessageValue"
                  rows="3"
                  placeholder="Custom message for pilots (optional)"
                ></textarea>
                <div class="form-text mb-3">
                  When empty, the default message "Contact this sponsor for pricing in your area." is shown to pilots.
                </div>
                <div class="d-flex gap-2">
                  <button class="btn btn-primary btn-sm" (click)="saveContactMessage()">Save</button>
                  <button class="btn btn-outline-secondary btn-sm" (click)="cancelEditContactMessage()">Cancel</button>
                </div>
              </div>
            </div>
          </div>

          <!-- ==========================================
               LOCATIONS TAB
               ========================================== -->
          <div *ngIf="activeTab === 'locations'">
            <app-sponsor-locations
              [sponsorId]="sponsorId"
              [contactType]="sponsor.contactType || 'contact_form_only'"
            ></app-sponsor-locations>
          </div>

          <!-- ==========================================
               REPRESENTATIVES TAB
               ========================================== -->
          <div *ngIf="activeTab === 'representatives'">
            <app-sponsor-representatives
              [sponsorId]="sponsorId"
              [contactType]="sponsor.contactType || 'contact_form_only'"
            ></app-sponsor-representatives>
          </div>

          <!-- ==========================================
               RATES TAB
               ========================================== -->
          <div *ngIf="activeTab === 'rates'">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h5 class="mb-0">
                <i class="bi bi-star me-2"></i>
                Point Rates
              </h5>
              <button class="btn btn-primary btn-sm" (click)="toggleAddRateForm()">
                <i class="bi" [class.bi-plus]="!showAddRateForm" [class.bi-x]="showAddRateForm"></i>
                {{ showAddRateForm ? 'Cancel' : 'Add Rate' }}
              </button>
            </div>

            <!-- Add Rate Form -->
            <div *ngIf="showAddRateForm" class="mb-4 pb-3 border-bottom">
              <h6 class="mb-3">New Point Rate</h6>
              <div *ngIf="addRateError" class="alert alert-danger py-2 mb-3">
                {{ addRateError }}
              </div>
              <div class="row g-3">
                <div class="col-md-3">
                  <label class="form-label">Chemical</label>
                  <select class="form-select" [(ngModel)]="newRate.chemicalId">
                    <option value="">Select chemical...</option>
                    <option *ngFor="let chem of sponsorChemicals" [value]="chem.id">
                      {{ chem.name }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Rate Value</label>
                  <input
                    type="number"
                    class="form-control"
                    [(ngModel)]="newRate.rateValue"
                    placeholder="e.g., 10"
                    min="0.01"
                    step="0.01"
                  />
                </div>
                <div class="col-md-2">
                  <label class="form-label">Rate Unit</label>
                  <select class="form-select" [(ngModel)]="newRate.rateUnit">
                    <option *ngFor="let unit of rateUnits" [value]="unit">
                      {{ unit }}
                    </option>
                  </select>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Calculation Mode</label>
                  <select class="form-select" [(ngModel)]="newRate.calculationMode">
                    <option *ngFor="let mode of calculationModes" [value]="mode.value">
                      {{ mode.label }}
                    </option>
                  </select>
                  <div class="form-text small">
                    {{ getSelectedCalculationModeDescription() }}
                  </div>
                </div>
                <div class="col-md-2">
                  <label class="form-label">Effective Start</label>
                  <input
                    type="date"
                    class="form-control"
                    [(ngModel)]="newRate.effectiveStart"
                  />
                </div>
                <div class="col-md-2">
                  <label class="form-label">Effective End</label>
                  <input
                    type="date"
                    class="form-control"
                    [(ngModel)]="newRate.effectiveEnd"
                    placeholder="Optional"
                  />
                </div>
              </div>
              <div class="mt-3">
                <button
                  class="btn btn-primary btn-sm"
                  (click)="submitRate()"
                  [disabled]="addingRate"
                >
                  <span *ngIf="addingRate" class="spinner-border spinner-border-sm me-1" role="status"></span>
                  {{ addingRate ? 'Creating...' : 'Create Rate' }}
                </button>
              </div>
            </div>

            <!-- Rates Table -->
            <div *ngIf="rates.length === 0 && !showAddRateForm" class="text-center py-5 text-muted">
              <i class="bi bi-star fs-1 d-block mb-3"></i>
              <p>No point rates configured for this sponsor.</p>
              <button class="btn btn-outline-primary btn-sm" (click)="toggleAddRateForm()">
                <i class="bi bi-plus me-1"></i>
                Add First Rate
              </button>
            </div>

            <div class="table-responsive" *ngIf="rates.length > 0">
              <table class="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>Chemical</th>
                    <th>Rate</th>
                    <th>Unit</th>
                    <th>Calculation Mode</th>
                    <th>Effective Start</th>
                    <th>Effective End</th>
                    <th>Status</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let rate of rates">
                    <td>{{ getChemicalName(rate.chemicalId) }}</td>
                    <td>{{ rate.rateValue }}</td>
                    <td>{{ rate.rateUnit }}</td>
                    <td>
                      <span class="badge" [ngClass]="rate.calculationMode === 'per_usage' ? 'bg-warning text-dark' : 'bg-primary'">
                        {{ getCalculationModeLabel(rate.calculationMode) }}
                      </span>
                    </td>
                    <td>{{ formatDate(rate.effectiveStart) }}</td>
                    <td>{{ formatDate(rate.effectiveEnd) }}</td>
                    <td>
                      <span class="badge" [ngClass]="getStatusBadgeClass(getRateStatus(rate))">
                        {{ getRateStatus(rate) }}
                      </span>
                    </td>
                    <td class="text-end">
                      <!-- End Rate action for active rates -->
                      <div *ngIf="getRateStatus(rate) === 'Active' && endingRateId !== rate.id">
                        <button
                          class="btn btn-outline-warning btn-sm"
                          (click)="startEndRate(rate.id)"
                          title="End this rate"
                        >
                          <i class="bi bi-stop-circle me-1"></i>
                          End Rate
                        </button>
                      </div>
                      <!-- End rate date picker (inline) -->
                      <div *ngIf="endingRateId === rate.id" class="d-flex align-items-center gap-2 justify-content-end">
                        <input
                          type="date"
                          class="form-control form-control-sm"
                          [(ngModel)]="endRateDate"
                          style="max-width: 160px;"
                        />
                        <button class="btn btn-warning btn-sm" (click)="confirmEndRate()">
                          Confirm
                        </button>
                        <button class="btn btn-outline-secondary btn-sm" (click)="cancelEndRate()">
                          Cancel
                        </button>
                      </div>
                      <!-- Expired/Upcoming indicator -->
                      <span *ngIf="getRateStatus(rate) === 'Expired'" class="text-muted small">
                        Expired
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</div>
